import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, catchError, of, switchMap, take } from 'rxjs';
import { Video } from '../../models/video.model';
import { VideoService } from '../../services/video.service';
import * as userActions from '../actions/user.actions';
import * as videoActions from '../actions/video.actions';

@Injectable()
export class VideoEffects {
  actions$ = inject(Actions);
  videoService: VideoService = inject(VideoService);
  toastr: ToastrService = inject(ToastrService);
  router: Router = inject(Router);

  private logout$ = new Subject<void>();

  constructor() {}

  // effect to fetch use videos from AWS using http call
  getVideos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(videoActions.getVideos),
      switchMap(
        async ({ uid }) =>
          await this.videoService.getVideos(uid).then((response) => response)
      ),
      switchMap((response: Observable<any>) =>
        response.pipe(
          take(1),
          switchMap((response) =>
            of(
              videoActions.loadVideo({
                videos: response['result'],
              })
            )
          )
        )
      ),
      catchError((error) => {
        console.error(`Error fetching videos: ${error}`);
        return of(videoActions.getVideosFailure({ error }));
      })
    )
  );

  getVideoById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(videoActions.getVideo),
      switchMap(
        async ({ id }) =>
          await this.videoService.getVideoById(id).then((response) => response)
      ),
      switchMap((response: Video) => {
        return of(
          videoActions.loadVideo({
            videos: [response],
          })
        );
      }),
      catchError((error) => {
        console.error(`Error fetching video: ${error}`);
        this.toastr.error(
          'Error fetching updates on the record. Please try again later',
          'Error Refreshing Video'
        );
        return of(videoActions.getVideosFailure({ error }));
      })
    )
  );

  // effect to create a video
  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(videoActions.create),
      switchMap(async ({ video }) => await this.videoService.create(video)),
      switchMap((video) => {
        this.toastr.success(
          "The Video is created in 'Draft' mode. You will be notified once it is published.",
          'Video Created Successfully'
        );
        this.router.navigate(['/videos']);
        return of(videoActions.createSuccess({ video }));
      }),
      catchError((error) => {
        // console.error(`Error creating video: ${error}`);
        this.toastr.error(
          `An error occurred while attempting to create the video. Please try again later.`,
          'Error Creating Video'
        );
        return of(videoActions.createFailure({ error }));
      })
    )
  );

  // effect to update a video
  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(videoActions.update),
      switchMap(async ({ video }) => await this.videoService.update(video)),
      switchMap((video) => {
        this.toastr.success(
          'Video details updated successfully. The information might take a while to refresh.',
          'Video Details Updated'
        );
        this.router.navigate(['/videos']);
        return of(videoActions.updateSuccess({ video }));
      }),
      catchError((error) => {
        // console.error(`Error updating video: ${error}`);
        this.toastr.error(
          `An error occurred while attempting to update the video. Please try again later.`,
          'Error Updating Video'
        );
        return of(
          videoActions.updateFailure({
            error: {
              code: 'VIDEO_UPDATE_ERROR',
              title: 'Video Update Exception',
              message: error.toString(),
            },
          })
        );
      })
    )
  );

  // effect to delete a video
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(videoActions.deleteAction),
      switchMap(
        async ({ id }) =>
          await this.videoService
            .delete(id)
            .then((resultId) => {
              this.toastr.success(
                'The video has been deleted successfully.',
                'Video Deleted'
              );
              this.router.navigate(['/videos']);
              return videoActions.deleteSuccess({ id: resultId });
            })
            .catch((error) => {
              this.toastr.error(
                `An error occurred while attempting to delete the video. Please try again later.`,
                'Error Deleting Video'
              );
              return videoActions.deleteFailure({ error: error.toString() });
            })
      )
    )
  );

  clearVideos$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(videoActions.clear());
      })
    )
  );
}
